import { ScreenBreakpoint } from "@crochik/pi-api";
import MenuIcon from "@mui/icons-material/Menu";
import { AppBar, Drawer, Toolbar, useTheme } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import * as muistyles from "@mui/styles";
import { Box } from "@mui/system";
import { observer } from "mobx-react";
import * as React from "react";
import app from "../../application/App";
import Dialog from "../../services/Dialog";
import PageComponent from "./app/PageComponent";
import TitleBarComponent from "./app/TitleBarComponent";
import ConfirmDialog from "./ConfirmDialog";
import DrawerComponent from "./DrawerComponent";
import DrawerToggleOverlay from "./DrawerToggleOverlay";
import { FormDialog } from "./FormDialog";
import SnackMessage from "./SnackMessage";

const drawerWidth = 260;

interface IProps {
  classes: any;
  theme: any;
  embedded: boolean;
  embeddedHeight?: number;
}

const ResponsiveDrawer: React.FC<IProps> = (props) => {
  const appStore = React.useMemo(() => app(), []);
  const [permanent, setPermanent] = React.useState(true);
  const [open, setOpen] = React.useState(true);

  const onlyTempMenu = React.useMemo(() => {
    switch (appStore.breakpoint) {
      case ScreenBreakpoint.Xs:
      case ScreenBreakpoint.Sm:
        return true;
      default:
        return false;
    }
  }, [appStore.breakpoint]);

  const isMenuPermanent = React.useMemo(() => {
    return !onlyTempMenu && permanent;
  }, [onlyTempMenu, permanent]);

  const isMenuOpen = React.useMemo(() => {
    if (!appStore.menu) return false;
    return open;
  }, [appStore.menu, open]);

  const togglePermanent = React.useCallback(() => {
    if (!appStore.menu) return;
    switch (appStore.breakpoint) {
      case ScreenBreakpoint.Xs:
      case ScreenBreakpoint.Sm:
        return;
      default:
        setPermanent((p) => !p);
        break;
    }
  }, [appStore.menu, appStore.breakpoint]);

  const openMenu = React.useCallback(() => {
    setPermanent(false);
    setOpen(true);
  }, []);

  const handleDrawerToggle = React.useCallback(() => {
    if (!appStore.menu) return;
    setOpen((o) => !o);
  }, [appStore.menu]);

  const onReload = React.useCallback(() => {
    document.location.reload();
  }, []);

  const theme = useTheme();
  const textColor = theme.palette.text.primary;
  const backgroundColor = theme.palette.primary.dark;

  React.useEffect(() => {
    if (!onlyTempMenu) {
      setOpen(true);
      setPermanent(true);
    }
  }, [onlyTempMenu]);

  const renderAppbar = React.useCallback(() => {
    if (!appStore.showTitleBar) return undefined;

    const { embedded } = props;

    if (!appStore.showMenu)
      return (
        <AppBar style={{ position: "relative" }}>
          <Toolbar>
            <TitleBarComponent embedded={embedded} />
          </Toolbar>
        </AppBar>
      );

    return (
      <AppBar position="fixed" style={{ position: "relative" }}>
        <Toolbar>
          {!isMenuOpen && (
            <IconButton
              color="inherit"
              onClick={openMenu}
              disabled={!appStore.menu}
              size="large"
            >
              <MenuIcon />
            </IconButton>
          )}
          <TitleBarComponent embedded={embedded} />
        </Toolbar>
      </AppBar>
    );
  }, [appStore, props, openMenu, isMenuOpen]);

  const renderDrawer = React.useCallback(() => {
    if (!appStore.showMenu) {
      return undefined;
    }

    if (isMenuPermanent) {
      return (
        <Box
          sx={{
            display: isMenuOpen ? "block" : "none",
            width: drawerWidth,
            flexShrink: 0,
          }}
          component="nav"
        >
          <Drawer
            variant="permanent"
            open={isMenuOpen}
            sx={{
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                width: drawerWidth,
                background: backgroundColor,
              },
            }}
            className="drawer"
          >
            <DrawerComponent
              onlyTempMenu={onlyTempMenu}
              onReload={onReload}
              isMenuPermanent={isMenuPermanent}
              togglePermanent={togglePermanent}
              handleDrawerToggle={handleDrawerToggle}
            />
          </Drawer>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          width: drawerWidth,
        }}
        component="nav"
      >
        <Drawer
          variant="temporary"
          anchor="left"
          open={isMenuOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              background: backgroundColor,
            },
          }}
        >
          <DrawerComponent
            onlyTempMenu={onlyTempMenu}
            onReload={onReload}
            isMenuPermanent={isMenuPermanent}
            togglePermanent={togglePermanent}
            handleDrawerToggle={handleDrawerToggle}
          />
        </Drawer>
      </Box>
    );
  }, [
    appStore.showMenu,
    onlyTempMenu,
    isMenuPermanent,
    isMenuOpen,
    handleDrawerToggle,
    togglePermanent,
    onReload,
  ]);

  const renderDialogForm = React.useCallback(() => {
    const { dialogs, closeDataFormDialogAsync, onDataFormDialogActionAsync } =
      app();
    if (dialogs.length < 1) return null;

    return dialogs.map((formDialog) => (
      <FormDialog
        key={formDialog.key}
        form={formDialog.form}
        onCloseAsync={closeDataFormDialogAsync}
        onActionAsync={onDataFormDialogActionAsync}
        skipTransition={true}
      />
    ));
  }, []);

  const renderBody = React.useCallback(() => {
    const sxBody = {
      width:
        isMenuPermanent && isMenuOpen
          ? `${appStore.innerWidth - drawerWidth}px`
          : `${appStore.innerWidth}px`,
    } as const;

    const { embedded } = props;

    return (
      <Box
        component="main"
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: "100%",
          position: "relative",
          boxSizing: "border-box",
        }}
      >
        {onlyTempMenu && renderAppbar()}
        <Box
          sx={{
            ...sxBody,
            height: "100%",
            overflow: "auto",
            position: "relative",
          }}
        >
          <PageComponent embedded={embedded} padding={0} />
        </Box>
      </Box>
    );
  }, [
    isMenuPermanent,
    isMenuOpen,
    appStore.innerWidth,
    props,
    renderAppbar,
    onlyTempMenu,
  ]);

  const dialog = Dialog.message;
  const snackBar = Dialog.snackBar;

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexGrow: 1,
          height: "100%",
          zIndex: 1,
          overflow: "hidden",
          position: "relative",
          width: "100%",
        }}
      >
        {renderDrawer()}
        {renderBody()}
      </Box>

      <DrawerToggleOverlay
        isMenuOpen={isMenuOpen}
        onOpenMenu={openMenu}
        isMenuEnabled={!!appStore.menu && appStore.showMenu}
      />

      {renderDialogForm()}
      {dialog && <ConfirmDialog {...dialog} />}
      {snackBar && <SnackMessage {...snackBar} />}
    </>
  );
};

export default muistyles.withTheme(observer(ResponsiveDrawer as any));
